import { useState } from "react";

import { useSelectedBuildingModelCarbonReductionPlan } from "hooks/Building/useBuildingModelCarbonReductionPlan";

// components
import { Header } from "components/atoms/Typography";
import ChartContainer from "components/molecules/ChartContainer";
import { ChartErrorState } from "components/molecules/ErrorStates";

// charts
import AverageMarginalAbatementCostByMeasureCategoryChart from "components/organisms/Charts/AverageMarginalAbatementCostByMeasureCategoryChart";
import AnnualCarbonEmissionsElectricityChart from "components/organisms/Charts/Recommendations/AnnualCarbonEmissionsElectricityChart";
import AnnualCarbonEmissionsNaturalGasChart from "components/organisms/Charts/Recommendations/AnnualCarbonEmissionsNaturalGasChart";
import AnnualEmissionSavingsByMeasureChart from "components/organisms/Charts/Recommendations/AnnualEmissionSavingsByMeasureChart";
import AnnualEmissionSavingsByMeasureTable, {
    MeasureDrillDown,
} from "components/organisms/Charts/Recommendations/AnnualEmissionSavingsByMeasureTable";
import CumulativeEmissionSavingsCard from "components/organisms/Charts/Recommendations/CumulativeEmissionSavingsCard";
import CumulativeInvestmentByDecarbonizationChart from "components/organisms/Charts/Recommendations/CumulativeInvestmentByDecarbonizationChart";
import { BuildingIncrementalNetPresentValueCard } from "components/organisms/Charts/Recommendations/CumulativeNetPresentValueCard";
import { BuildingCumulativeOperatingCostCard } from "components/organisms/Charts/Recommendations/CumulativeOperatingCostCard";
import FutureProjectionCards from "components/organisms/Charts/Recommendations/FutureProjectionCards";

// hooks
import { useAnnualCarbonEmissionsCard } from "queries/BuildingReport/Recommendations/useAnnualCarbonEmissionsCard";
import { useAnnualCarbonEmissionsChart } from "queries/BuildingReport/Recommendations/useAnnualCarbonEmissionsChart";
import { useAnnualCarbonEmissionsElectricityChart } from "queries/BuildingReport/Recommendations/useAnnualCarbonEmissionsElectricityChart";
import { useAnnualCarbonEmissionsNaturalGasChart } from "queries/BuildingReport/Recommendations/useAnnualCarbonEmissionsNaturalGasChart";
import { useAnnualEmissionSavingsByMeasureChart } from "queries/BuildingReport/Recommendations/useAnnualEmissionSavingsByMeasureChart";
import { useAnnualEmissionSavingsByMeasureTable } from "queries/BuildingReport/Recommendations/useAnnualEmissionSavingsByMeasureTable";
import { useAnnualEnergyConsumptionCard } from "queries/BuildingReport/Recommendations/useAnnualEnergyConsumptionCard";
import { useAverageMarginalAbatementCostByMeasureCategoryChart } from "queries/BuildingReport/Recommendations/useAverageMarginalAbatementCostByMeasureCategoryChart";
import { useCumulativeCarbonEmissionsCard } from "queries/BuildingReport/Recommendations/useCumulativeCarbonEmissionsCard";
import { useCumulativeEmissionSavingsCard } from "queries/BuildingReport/Recommendations/useCumulativeEmissionSavingsCard";
import { useCumulativeEnergyConsumptionCard } from "queries/BuildingReport/Recommendations/useCumulativeEnergyConsumptionCard";
import { useCumulativeInvestmentByDecarbonizationChart } from "queries/BuildingReport/Recommendations/useCumulativeInvestmentByDecarbonizationChart";

// constants
import { CURRENT_YEAR } from "utils/constants";

// styling
import AnnualCarbonEmissionsChartWithRecs from "components/organisms/Charts/Recommendations/AnnualCarbonEmissionsChartWithRecs";
import { BuildingAnnualCashFlowChartRecommendations } from "components/organisms/Charts/Recommendations/AnnualCashFlowChartRecommendations";
import "./DecarbonizationReport.css";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";

function DecarbonizationReport() {
    const {
        bmcrp: selectedBMCRP,
        buildingModel,
        isLoading: isLoadingBMCRP,
    } = useSelectedBuildingModelCarbonReductionPlan();

    if (isLoadingBMCRP || !selectedBMCRP || !buildingModel) {
        return (
            <div className="decarbonization-building-report">
                <LoadingSpinner />
            </div>
        );
    }
    return (
        <div className="decarbonization-building-report">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <div className="recommendations-report--title">
                    <Header>Reach your goal</Header>
                    <Header size="x-small" style={{ color: "var(--audette-gray-500)" }}>
                        {CURRENT_YEAR} - 2050
                    </Header>
                </div>
            </div>
            <AnnualEmissionSavingsByMeasures />
            <div className="recommendations-report--title">
                <Header>Financial impact</Header>
                <Header size="x-small" style={{ color: "var(--audette-gray-500)" }}>
                    {CURRENT_YEAR} - 2050
                </Header>
            </div>
            <div className="cards-and-chart-grid">
                <div className="cards">
                    <BuildingIncrementalNetPresentValueCard
                        buildingModel={buildingModel}
                        selectedBMCRP={selectedBMCRP}
                    />
                    <BuildingCumulativeOperatingCostCard
                        buildingModel={buildingModel}
                        selectedBMCRP={selectedBMCRP}
                        isBaseline={false}
                        year={null}
                    />
                </div>
                <BuildingAnnualCashFlowChartRecommendations
                    buildingModel={buildingModel}
                    selectedBMCRP={selectedBMCRP}
                />
            </div>
            <div className="side-by-side-charts-grid">
                <AverageMarginalAbatementCostByMeasureCategoryChart
                    query={useAverageMarginalAbatementCostByMeasureCategoryChart}
                />
                <CumulativeInvestmentByDecarbonizationChart query={useCumulativeInvestmentByDecarbonizationChart} />
            </div>
            <Header>Future projection</Header>
            <div className="cards-and-chart-grid">
                <FutureProjectionCards
                    cumulativeCarbonEmissionsCardQuery={useCumulativeCarbonEmissionsCard}
                    cumulativeEnergyConsumptionCardQuery={useCumulativeEnergyConsumptionCard}
                    annualCarbonEmissionsCardQuery={useAnnualCarbonEmissionsCard}
                    annualEnergyConsumptionCardQuery={useAnnualEnergyConsumptionCard}
                />
                <AnnualCarbonEmissionsChartWithRecs query={useAnnualCarbonEmissionsChart} />
            </div>
            <div className="side-by-side-charts-grid">
                <AnnualCarbonEmissionsNaturalGasChart query={useAnnualCarbonEmissionsNaturalGasChart} />
                <AnnualCarbonEmissionsElectricityChart query={useAnnualCarbonEmissionsElectricityChart} />
            </div>
        </div>
    );
}

function AnnualEmissionSavingsByMeasures() {
    const [showChart, setShowChart] = useState(true);
    const { isLoading: chartIsLoading, error: chartError } = useAnnualEmissionSavingsByMeasureChart();
    const { data, isLoading: tableIsLoading, error: tableError } = useAnnualEmissionSavingsByMeasureTable();

    if (chartError || tableError) return <ChartErrorState />;

    return (
        <ChartContainer
            title="Decarbonization measure types"
            loading={chartIsLoading || tableIsLoading}
            onClickTable={() => setShowChart((show) => !show)}
            exportableData={data}
        >
            {showChart ? (
                <>
                    <AnnualEmissionSavingsByMeasureChart query={useAnnualEmissionSavingsByMeasureChart} />
                    <CumulativeEmissionSavingsCard query={useCumulativeEmissionSavingsCard} />
                </>
            ) : (
                <AnnualEmissionSavingsByMeasureTable
                    query={useAnnualEmissionSavingsByMeasureTable}
                    drillDown={MeasureDrillDown.Type}
                />
            )}
        </ChartContainer>
    );
}

export default DecarbonizationReport;
